import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../Card/Card';
import styles from './InsightsIA.module.css';

const TONE_TO_DOT = {
  good:    styles.dotG,
  bad:     styles.dotR,
  warning: styles.dotO,
};

/**
 * InsightsIA — liste d'insights générés par l'IA.
 *
 * Props :
 *  - title    : "Insights IA"
 *  - badge    : "3 actions"  (n'est pas affiché s'il n'y a pas d'insights)
 *  - insights : [{ tone:'good'|'bad'|'warning', icon, text, sub, linkLabel?, onLinkClick? }]
 *               | [] | null | undefined
 *    `text` peut contenir du JSX (ex: `<strong>...</strong>`).
 *
 * ID préservé : #insights-ia
 *
 * ⚠️ Hotfix 04/05/2026 — Les 3 mocks (ambiance provençale 71%, attente service
 * midi, 5 avis sans réponse) ont été retirés. Si `insights` est vide / absent,
 * on affiche un empty state plutôt que de polluer le dashboard de tous les
 * restos qui n'ont pas encore généré d'insights.
 */
export default function InsightsIA({
  title,
  badge,
  insights,
}) {
  const { t } = useTranslation('dashboard');
  const safeTitle = title || t('insights_ia.default_title');
  const hasData = Array.isArray(insights) && insights.length > 0;

  return (
    <Card span={6} id="insights-ia">
      <h2 id="insights-card-header" className={styles.title}>
        <span className={styles.icon}>✨</span> {safeTitle}
        {badge && hasData && <span id="insights-badge" className={styles.badge}>{badge}</span>}
      </h2>

      {hasData ? (
        insights.map((ins, i) => (
          <div key={i} className={styles.item}>
            <div className={`${styles.dot} ${TONE_TO_DOT[ins.tone] || styles.dotO}`}>
              {ins.icon}
            </div>
            <div>
              <div className={styles.text}>{ins.text}</div>
              {ins.sub && <div className={styles.sub}>{ins.sub}</div>}
              {ins.linkLabel && (
                <button
                  type="button"
                  className={styles.link}
                  onClick={ins.onLinkClick}
                >
                  {ins.linkLabel}
                </button>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className={styles.empty}>
          <span className={styles.emptyEmoji}>{t('insights_ia.empty_emoji')}</span>
          <span className={styles.emptyTitle}>{t('insights_ia.empty_title')}</span>
          <span className={styles.emptyHint}>{t('insights_ia.empty_hint')}</span>
        </div>
      )}
    </Card>
  );
}
